import {
  CiclosPorTipo,
  EjecucionPrueba,
  EjecucionPruebaPeticion,
  EvidenciaPrueba,
  IndicadoresVersion,
  LARGO_OBSERVACIONES_PRUEBA,
  MAXIMO_BYTES_PETICION_PRUEBA,
  MAXIMO_EVIDENCIAS_PRUEBA,
  ResultadoPrueba,
  TipoPrueba,
  UltimaPrueba,
  VersionResumen,
} from "./etapas-versiones.model";
import { ChipEstado, SIN_DATO, diaDeTexto, diaEnMexico, fechaCorta, fechaDeVersion } from "./etapas-versiones.util";

/*
 * Ejecuciones de pruebas (T2) y detalle de versión (A2): funciones puras
 * (plan docs/superpowers/plans/2026-10-13-etapas-y-versiones.md §5.7 y §8).
 * No deciden reglas que no estén en el servidor:
 *
 * - los nombres de tipo, ciclo y resultado, y la «Última prueba» de A0b y A1;
 * - validarEjecucion, la copia de ReglasEjecucionPrueba (los mismos
 *   obligatorios, límites de fecha, casos y resultado, con sus mensajes);
 * - la evidencia: la lista blanca de D17, 10 archivos por ejecución y los
 *   50 MB por petición, para avisar antes de subir (el servidor revisa lo
 *   mismo y además la firma de los bytes);
 * - la petición de PROYECTOS_PRUEBAS_GUARDAR y los textos de A2.
 */

/** Un tipo de prueba con su nombre (TipoPruebaEnum.getNombre). */
export interface OpcionTipoPrueba {
  clave: TipoPrueba;
  nombre: string;
}

/** Un resultado con su nombre, su explicación (maqueta T2) y su chip. */
export interface OpcionResultado {
  clave: ResultadoPrueba;
  nombre: string;
  descripcion: string;
  clase: string;
  icono: string;
}

export const TIPOS_PRUEBA: readonly OpcionTipoPrueba[] = [
  { clave: "FUNCIONAL", nombre: "Funcional" },
  { clave: "REGRESION", nombre: "Regresión" },
  { clave: "HUMO", nombre: "Humo" },
  { clave: "ACEPTACION_USUARIO", nombre: "Aceptación del usuario" },
];

export const RESULTADOS_PRUEBA: readonly OpcionResultado[] = [
  { clave: "APROBADA", nombre: "Aprobada", descripcion: "Todos los casos pasaron.", clase: "is-active", icono: "check_circle" },
  { clave: "CON_INCIDENCIAS", nombre: "Con incidencias", descripcion: "Hubo casos fallidos.", clase: "is-pending", icono: "report_problem" },
  { clave: "BLOQUEADA", nombre: "Bloqueada", descripcion: "No se pudo completar la ejecución.", clase: "is-danger", icono: "block" },
];

/** Los campos de T2 que puede rechazar validarEjecucion; «casos» son las reglas entre casos y resultado. */
export type CampoEjecucion =
  | "idVersion"
  | "tipo"
  | "fechaEjecucion"
  | "idEmpleadoEjecuto"
  | "resultado"
  | "casosEjecutados"
  | "casosAprobados"
  | "casosFallidos"
  | "casos"
  | "observaciones";

export type ErroresEjecucion = Partial<Record<CampoEjecucion, string>>;

/** Lo capturado en T2; la fecha ya como yyyy-MM-dd. */
export interface ValoresEjecucion {
  idVersion: string | null;
  tipo: TipoPrueba | null;
  fechaEjecucion: string | null;
  idEmpleadoEjecuto: number | null;
  resultado: ResultadoPrueba | null;
  casosEjecutados: number | null;
  casosAprobados: number | null;
  casosFallidos: number | null;
  observaciones: string | null;
}

/** Los días (yyyy-MM-dd) entre los que puede caer la fecha de ejecución: hoy y, si se sabe, la creación de la versión. */
export interface LimitesFecha {
  hoy: string;
  desde: string | null;
}

/** Un archivo que se revisa antes de subirlo (File, o lo que se le parezca en las pruebas). */
export interface ArchivoPorRevisar {
  name: string;
  size: number;
}

/** Los archivos que sí entran y por qué no entraron los demás. */
export interface RevisionDeArchivos<T extends ArchivoPorRevisar> {
  aceptados: T[];
  rechazos: string[];
}

/** La «Última prueba» de A0b y A1, ya en textos. */
export interface TextoUltimaPrueba {
  /** «Regresión 2», «Funcional, ciclo 1». */
  titulo: string;
  /** dd/MM/yyyy; null si no se puede leer. */
  fecha: string | null;
  /** «115/120»; null sin casos. */
  casos: string | null;
  /** % de aprobados (la barra); null sin casos. */
  porcentaje: number | null;
  chip: ChipEstado | null;
}

/** D17: la lista blanca de EvidenciaPruebaArchivos. */
export const EXTENSIONES_EVIDENCIA: ReadonlySet<string> = new Set([
  "pdf",
  "png",
  "jpg",
  "jpeg",
  "gif",
  "xlsx",
  "xls",
  "csv",
  "docx",
  "doc",
  "txt",
  "log",
  "zip",
]);

/** El accept del selector de archivos (la misma lista). */
export const ACEPTA_EVIDENCIA = Array.from(EXTENSIONES_EVIDENCIA, (extension) => `.${extension}`).join(",");

/** Lo que dice el rechazo de una extensión (EvidenciaPruebaArchivos.FORMATOS). */
export const FORMATOS_EVIDENCIA = "PDF, imágenes (PNG, JPG, GIF), Excel (XLSX, XLS), CSV, Word (DOCX, DOC), TXT, LOG y ZIP, sin video";

/** Las incidencias de A2 que se ven antes de «Ver las N». */
export const INCIDENCIAS_VISIBLES = 5;

const MEGABYTE = 1024 * 1024;
const ISO_DIA = /^(\d{4})-(\d{2})-(\d{2})$/;

/* ============================ Tipos, ciclos y resultados ============================ */

/** «Regresión», «Aceptación del usuario»; el texto tal cual si no es un tipo conocido. */
export function nombreTipoPrueba(tipo: string | null | undefined): string {
  return TIPOS_PRUEBA.find((t) => t.clave === tipo)?.nombre ?? ((tipo ?? "").trim() || "Prueba");
}

/**
 * El nombre de una ejecución por su ciclo (D16), como A1 y T2: «Regresión 2»
 * y, en los demás tipos, «Funcional, ciclo 1». Sin ciclo, solo el tipo.
 */
export function nombreCiclo(tipo: string | null | undefined, ciclo: number | null | undefined): string {
  const nombre = nombreTipoPrueba(tipo);
  if (ciclo === null || ciclo === undefined) {
    return nombre;
  }
  return tipo === "REGRESION" ? `${nombre} ${ciclo}` : `${nombre}, ciclo ${ciclo}`;
}

/** El ciclo que tomará la siguiente ejecución del tipo (el servidor lo asigna; 1 si no lo dice). */
export function cicloSiguiente(ciclos: CiclosPorTipo | null | undefined, tipo: TipoPrueba): number {
  return Math.max(ciclos?.[tipo] ?? 1, 1);
}

/** «Se registrará como «Regresión 2»»; null sin tipo. */
export function textoSeRegistraComo(ciclos: CiclosPorTipo | null | undefined, tipo: TipoPrueba | null | undefined): string | null {
  return tipo ? `Se registrará como «${nombreCiclo(tipo, cicloSiguiente(ciclos, tipo))}»` : null;
}

/** El chip de un resultado: Aprobada (verde), Con incidencias (ámbar) o Bloqueada (rojo). */
export function chipResultado(resultado: string | null | undefined): ChipEstado {
  const opcion = RESULTADOS_PRUEBA.find((r) => r.clave === resultado);
  return opcion
    ? { texto: opcion.nombre, clase: opcion.clase, icono: opcion.icono, detalle: null }
    : { texto: SIN_DATO, clase: "is-inactive", icono: "help_outline", detalle: null };
}

/** % de casos aprobados, sin decimales; null sin ejecutados. */
export function porcentajeAprobados(aprobados: number | null | undefined, ejecutados: number | null | undefined): number | null {
  if (!ejecutados || ejecutados <= 0 || aprobados === null || aprobados === undefined) {
    return null;
  }
  return Math.max(0, Math.min(100, Math.round((aprobados / ejecutados) * 100)));
}

/** La «Última prueba» de A0b y A1 (tipo y ciclo, fecha, aprobados/ejecutados y chip); null sin ejecuciones. */
export function textoUltimaPrueba(ultima: UltimaPrueba | null | undefined): TextoUltimaPrueba | null {
  if (!ultima) {
    return null;
  }
  const hayCasos = typeof ultima.aprobados === "number" && typeof ultima.ejecutados === "number";
  return {
    titulo: nombreCiclo(ultima.tipo, ultima.ciclo),
    fecha: fechaCorta(ultima.fecha),
    casos: hayCasos ? `${ultima.aprobados}/${ultima.ejecutados}` : null,
    porcentaje: porcentajeAprobados(ultima.aprobados, ultima.ejecutados),
    chip: ultima.resultado ? chipResultado(ultima.resultado) : null,
  };
}

/* ============================ Validación (ReglasEjecucionPrueba) ============================ */

/**
 * Los límites de la fecha de ejecución: hoy en la Ciudad de México y el día
 * en que se creó la versión, si tiene fechaCreacion (la «aprox.» del nombre
 * no cuenta, como en el servidor).
 */
export function limitesDeFecha(fechaCreacion: string | null | undefined, ahora: Date): LimitesFecha {
  return { hoy: diaEnMexico(ahora), desde: diaDeTexto(fechaCreacion) };
}

/** «Entre el 13/08/2026 y hoy» o «Hasta hoy». */
export function textoLimitesFecha(limites: LimitesFecha): string {
  const desde = fechaCorta(limites.desde);
  return desde ? `Entre el ${desde} y hoy` : "Hasta hoy";
}

/**
 * Las reglas de §5.7, con los mensajes del servidor: un campo sin error no
 * sale en el resultado. Vacío = se puede guardar.
 */
export function validarEjecucion(valores: ValoresEjecucion, limites: LimitesFecha): ErroresEjecucion {
  const errores: ErroresEjecucion = { ...erroresDeCasos(valores) };
  if (!valores.idVersion) {
    errores.idVersion = "Elige la versión que se probó.";
  }
  if (!valores.tipo) {
    errores.tipo = "Elige el tipo de prueba.";
  }
  const fecha = errorDeFecha(valores.fechaEjecucion, limites);
  if (fecha) {
    errores.fechaEjecucion = fecha;
  }
  if (valores.idEmpleadoEjecuto === null) {
    errores.idEmpleadoEjecuto = "Elige quién ejecutó la prueba.";
  }
  if (!valores.resultado) {
    errores.resultado = "Elige el resultado de la ejecución.";
  }
  const observaciones = errorDeObservaciones(valores);
  if (observaciones) {
    errores.observaciones = observaciones;
  }
  return errores;
}

/** La fecha de ejecución: obligatoria, válida, no futura y no anterior a la creación de la versión. */
function errorDeFecha(fecha: string | null, limites: LimitesFecha): string | null {
  if (!fecha) {
    return "Escribe la fecha de ejecución.";
  }
  if (!ISO_DIA.test(fecha) || !diaDeTexto(fecha)) {
    return "La fecha de ejecución no es una fecha válida.";
  }
  if (fecha > limites.hoy) {
    return "La fecha de ejecución no puede ser posterior a hoy.";
  }
  if (limites.desde && fecha < limites.desde) {
    return `La fecha de ejecución no puede ser anterior a la creación de la versión (${fechaCorta(limites.desde)}).`;
  }
  return null;
}

/** Ejecutados 1 o más, aprobados y fallidos 0 o más (enteros), su suma y lo que pide cada resultado. */
function erroresDeCasos(valores: ValoresEjecucion): ErroresEjecucion {
  const errores: ErroresEjecucion = {};
  const { casosEjecutados: ejecutados, casosAprobados: aprobados, casosFallidos: fallidos } = valores;
  if (!esEntero(ejecutados) || ejecutados < 1) {
    errores.casosEjecutados = "Los casos ejecutados son 1 o más.";
  }
  if (!esEntero(aprobados) || aprobados < 0) {
    errores.casosAprobados = "Escribe los casos aprobados (0 o más).";
  }
  if (!esEntero(fallidos) || fallidos < 0) {
    errores.casosFallidos = "Escribe los casos fallidos (0 o más).";
  }
  if (Object.keys(errores).length > 0) {
    return errores;
  }
  const regla = reglaDeCasos(valores.resultado, ejecutados as number, aprobados as number, fallidos as number);
  return regla ? { casos: regla } : {};
}

function reglaDeCasos(resultado: ResultadoPrueba | null, ejecutados: number, aprobados: number, fallidos: number): string | null {
  if (aprobados + fallidos > ejecutados) {
    return "Los aprobados y los fallidos no pueden sumar más que los ejecutados.";
  }
  if (resultado === "APROBADA" && (fallidos !== 0 || aprobados !== ejecutados)) {
    return "Una ejecución aprobada no tiene casos fallidos y tiene todos sus casos aprobados.";
  }
  if (resultado === "CON_INCIDENCIAS" && fallidos < 1) {
    return "Una ejecución con incidencias tiene al menos un caso fallido.";
  }
  return null;
}

/** Hasta 2000 caracteres y, en una bloqueada, obligatorias (por qué no se completó). */
function errorDeObservaciones(valores: ValoresEjecucion): string | null {
  const texto = (valores.observaciones ?? "").trim();
  if (texto.length > LARGO_OBSERVACIONES_PRUEBA) {
    return `Las observaciones no pueden pasar de ${LARGO_OBSERVACIONES_PRUEBA} caracteres.`;
  }
  if (valores.resultado === "BLOQUEADA" && !texto) {
    return "Escribe en las observaciones por qué no se pudo completar la ejecución.";
  }
  return null;
}

function esEntero(valor: number | null): valor is number {
  return typeof valor === "number" && Number.isInteger(valor);
}

/* ============================ Fechas del selector ============================ */

/** yyyy-MM-dd del día que eligió el calendario (fecha local, sin zona). */
export function diaDeFechaLocal(fecha: Date | null | undefined): string | null {
  if (!(fecha instanceof Date) || Number.isNaN(fecha.getTime())) {
    return null;
  }
  const mes = String(fecha.getMonth() + 1).padStart(2, "0");
  const dia = String(fecha.getDate()).padStart(2, "0");
  return `${fecha.getFullYear()}-${mes}-${dia}`;
}

/** La fecha local (medianoche) de un yyyy-MM-dd, para el calendario; null si no es un día válido. */
export function fechaLocalDeDia(dia: string | null | undefined): Date | null {
  const partes = ISO_DIA.exec((dia ?? "").trim());
  if (!partes || !diaDeTexto(partes[0])) {
    return null;
  }
  return new Date(+partes[1], +partes[2] - 1, +partes[3]);
}

/* ============================ Petición ============================ */

/**
 * La parte «ejecucion» de PROYECTOS_PRUEBAS_GUARDAR, con lo ya validado:
 * observaciones recortadas (vacías = null) e incidencias sin repetir. En una
 * edición, claves.fechaActualizacion es la de la ejecución como llegó (el
 * sello con que el servidor detecta que otro la editó en medio).
 */
export function peticionEjecucion(
  claves: { id: string | null; idProyecto: string; idEtapa: string; fechaActualizacion: string | null },
  valores: ValoresEjecucion,
  incidencias: readonly number[],
  evidenciasQuitar: readonly string[],
): EjecucionPruebaPeticion {
  const observaciones = (valores.observaciones ?? "").trim();
  return {
    id: claves.id,
    idProyecto: claves.idProyecto,
    idEtapa: claves.idEtapa,
    idVersion: valores.idVersion ?? "",
    tipo: valores.tipo as TipoPrueba,
    fechaEjecucion: valores.fechaEjecucion ?? "",
    idEmpleadoEjecuto: valores.idEmpleadoEjecuto as number,
    resultado: valores.resultado as ResultadoPrueba,
    casosEjecutados: valores.casosEjecutados ?? 0,
    casosAprobados: valores.casosAprobados ?? 0,
    casosFallidos: valores.casosFallidos ?? 0,
    incidencias: Array.from(new Set(incidencias)),
    observaciones: observaciones || null,
    evidenciasQuitar: [...evidenciasQuitar],
    fechaActualizacion: claves.fechaActualizacion,
  };
}

/* ============================ Evidencia ============================ */

/** La extensión en minúsculas («pdf»); vacía si no tiene. */
export function extensionDe(nombre: string | null | undefined): string {
  const limpio = (nombre ?? "").trim();
  const punto = limpio.lastIndexOf(".");
  return punto > 0 && punto < limpio.length - 1 ? limpio.slice(punto + 1).toLowerCase() : "";
}

/**
 * Los archivos que se pueden agregar: no vacíos, de la lista blanca, hasta
 * 10 por ejecución (con los que ya tiene) y hasta 50 MB entre los nuevos de
 * la petición. Los demás salen en rechazos, cada uno con su motivo.
 *
 * @param yaHay las evidencias que se quedan más las nuevas ya agregadas.
 * @param bytesYa lo que pesan las nuevas ya agregadas.
 */
export function revisarEvidencias<T extends ArchivoPorRevisar>(
  nuevos: readonly T[],
  yaHay: number,
  bytesYa: number,
): RevisionDeArchivos<T> {
  const aceptados: T[] = [];
  const rechazos: string[] = [];
  let cuantos = yaHay;
  let bytes = bytesYa;
  for (const archivo of nuevos) {
    const motivo = motivoDeRechazo(archivo, cuantos, bytes);
    if (motivo) {
      rechazos.push(motivo);
      continue;
    }
    aceptados.push(archivo);
    cuantos++;
    bytes += archivo.size;
  }
  return { aceptados, rechazos };
}

function motivoDeRechazo(archivo: ArchivoPorRevisar, cuantos: number, bytes: number): string | null {
  const nombre = (archivo.name ?? "").trim() || "sin nombre";
  if (!archivo.size || archivo.size <= 0) {
    return `El archivo «${nombre}» está vacío.`;
  }
  if (!EXTENSIONES_EVIDENCIA.has(extensionDe(nombre))) {
    return `El archivo «${nombre}» no es de un tipo permitido. Se aceptan ${FORMATOS_EVIDENCIA}.`;
  }
  if (cuantos >= MAXIMO_EVIDENCIAS_PRUEBA) {
    return `Una ejecución lleva hasta ${MAXIMO_EVIDENCIAS_PRUEBA} archivos de evidencia: «${nombre}» no se agregó.`;
  }
  if (bytes + archivo.size > MAXIMO_BYTES_PETICION_PRUEBA) {
    return `Los archivos nuevos no pueden pasar de ${MAXIMO_BYTES_PETICION_PRUEBA / MEGABYTE} MB por guardado: «${nombre}» no se agregó.`;
  }
  return null;
}

/** «850 B», «120 KB», «1.4 MB». */
export function tamanoLegible(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined || bytes < 0) {
    return SIN_DATO;
  }
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < MEGABYTE) {
    return `${Math.round(bytes / 1024)} KB`;
  }
  return `${(Math.round((bytes / MEGABYTE) * 10) / 10).toString()} MB`;
}

/** «PDF · 120 KB»: el renglón de un archivo bajo su nombre. */
export function lineaArchivo(nombre: string | null | undefined, bytes: number | null | undefined): string {
  const extension = extensionDe(nombre).toUpperCase();
  return [extension || "Archivo", tamanoLegible(bytes)].join(" · ");
}

/** El nombre con que se subió la evidencia; el del disco si no lo tiene. */
export function nombreDeEvidencia(evidencia: Pick<EvidenciaPrueba, "archivo" | "nombreOriginal">): string {
  return evidencia.nombreOriginal?.trim() || evidencia.archivo;
}

/* ============================ Textos de A2 y de la edición ============================ */

/** Bajo «Casos ejecutados» (D20): «En 1 ejecución», «Última de 3 ejecuciones» o «Sin ejecuciones registradas». */
export function textoCasosEjecutados(indicadores: Pick<IndicadoresVersion, "ejecuciones"> | null | undefined): string {
  const ejecuciones = indicadores?.ejecuciones ?? 0;
  if (ejecuciones <= 0) {
    return "Sin ejecuciones registradas";
  }
  return ejecuciones === 1 ? "En 1 ejecución" : `Última de ${ejecuciones} ejecuciones`;
}

/** «de 120 casos» bajo aprobados y fallidos; vacío sin ejecuciones. */
export function textoDeCasos(ejecutados: number | null | undefined): string {
  if (typeof ejecutados !== "number") {
    return "";
  }
  return ejecutados === 1 ? "de 1 caso" : `de ${ejecutados} casos`;
}

/** «· 115 aprobados · 5 fallidos» junto a los casos de una ejecución. */
export function lineaCasos(ejecucion: Pick<EjecucionPrueba, "casosAprobados" | "casosFallidos">): string {
  const aprobados = ejecucion.casosAprobados ?? 0;
  const fallidos = ejecucion.casosFallidos ?? 0;
  return `· ${aprobados} ${aprobados === 1 ? "aprobado" : "aprobados"} · ${fallidos} ${fallidos === 1 ? "fallido" : "fallidos"}`;
}

/** «Registrada el 14/08/2026 · última edición 15/08/2026» (o «· sin ediciones»). */
export function textoRegistroEjecucion(ejecucion: Pick<EjecucionPrueba, "fechaRegistro" | "fechaActualizacion">): string {
  const registro = fechaCorta(ejecucion.fechaRegistro);
  const edicion = fechaCorta(ejecucion.fechaActualizacion);
  const inicio = registro ? `Registrada el ${registro}` : "Registrada";
  const fin = edicion ? `última edición ${edicion}` : "sin ediciones";
  return `${inicio} · ${fin}`;
}

/**
 * «Creada el 13/08/2026 por Irving Guzmán Salas», «Creada el 16/07/2026
 * (aprox.) · autor no registrado» (D15) o «Creada: — · autor no registrado»
 * (§4.1).
 */
export function textoCreadaPor(version: Pick<VersionResumen, "nombre" | "fechaCreacion" | "fechaAprox" | "nombreUsuarioCrea">): string {
  const fecha = fechaDeVersion(version);
  const cuando = fecha === SIN_DATO ? "Creada: —" : `Creada el ${fecha}`;
  const autor = version.nombreUsuarioCrea?.trim();
  return autor ? `${cuando} por ${autor}` : `${cuando} · autor no registrado`;
}

/** Las primeras N (o todas): «Mostrando 5 de 14 · Ver las 14» de A2. */
export function primeras<T>(lista: readonly T[] | null | undefined, todas: boolean, limite = INCIDENCIAS_VISIBLES): T[] {
  const completa = lista ?? [];
  return todas ? [...completa] : completa.slice(0, limite);
}

/** «Mostrando 5 de 14 incidencias». */
export function textoMostrando(mostradas: number, total: number, singular: string, plural: string): string {
  return `Mostrando ${mostradas} de ${total} ${total === 1 ? singular : plural}`;
}
